import { useCallback, useRef } from 'react';

/**
 * Returns a CALLBACK REF to put on a scrollable element so it responds to the mouse wheel even when
 * it's portalled OUTSIDE an open Radix Dialog (modal). A modal locks page scroll via
 * `react-remove-scroll`, which `preventDefault`s every wheel event that lands outside the dialog's DOM
 * subtree — and Popover/Dropdown content is portalled to <body>, i.e. outside it. The native scroll is
 * therefore suppressed and the list can't scroll by wheel (only by dragging the scrollbar / arrow keys).
 *
 * A callback ref (not useEffect) is used deliberately: Radix mounts portalled content on its own
 * schedule, so an effect keyed on `open` can run before the node exists and then never re-attach. The
 * callback ref fires exactly when the node mounts/unmounts, so the listener is always attached to the
 * live element.
 *
 * The handler attaches a NON-passive wheel listener (React's onWheel is passive, so it can't
 * preventDefault) that scrolls the element itself and cancels the native scroll — one consistent,
 * single scroll whether or not a modal is open. No-op while the element has nothing to scroll, so
 * page-scroll still chains normally for short/empty lists.
 */
export function useWheelScroll<T extends HTMLElement = HTMLElement>(): (node: T | null) => void {
  const cleanup = useRef<(() => void) | null>(null);
  return useCallback((node: T | null) => {
    // Detach from any previous node first (remount / unmount).
    cleanup.current?.();
    cleanup.current = null;
    if (!node) return;
    const onWheel = (e: WheelEvent) => {
      // Nothing to scroll → let the event bubble (page/parent handles it as usual).
      if (node.scrollHeight <= node.clientHeight) return;
      // We own the scroll: cancel the native one (which a modal's lock would otherwise eat) and move
      // the list ourselves. deltaX is ignored — these lists are vertical.
      e.preventDefault();
      node.scrollTop += e.deltaY;
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    cleanup.current = () => node.removeEventListener('wheel', onWheel);
  }, []);
}
